import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowRightIcon,
  BuildingStorefrontIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  ConfirmDialog,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  FormCard,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  QUANTITY_STEP,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import {
  useActiveWarehouses,
  useCancelStockTransfer,
  useCreateStockTransfer,
  useDeleteStockTransfer,
  useReceiveStockTransfer,
  useShipStockTransfer,
  useStockTransfer,
  useStockTransfersPage,
  useStockTransferSummary,
  useUpdateStockTransfer,
  useWarehouseStock,
} from '../../hooks/useWarehouses';
import { StockTransfer, StockTransferInput, StockTransferStatus } from '../../services/warehouse.service';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const STATUS: Record<StockTransferStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  pending: { label: 'Draft', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  in_transit: { label: 'In transit', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  received: { label: 'Received', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 ring-red-600/20' },
};
const isDraft = (status: StockTransferStatus) => status === 'draft' || status === 'pending';

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StatusBadge: React.FC<{ status: StockTransferStatus }> = ({ status }) => (
  <span className={cn(badgeClass, STATUS[status]?.className)}>{STATUS[status]?.label ?? status}</span>
);

interface FormLine {
  productId: string;
  name: string;
  sku: string;
  unit: string;
  available: number;
  quantity: string;
}

interface TransferFormProps {
  transfer: StockTransfer | null;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: StockTransferInput) => void;
}

const TransferForm: React.FC<TransferFormProps> = ({ transfer, isPending, onCancel, onSubmit }) => {
  const isEdit = !!transfer;
  const { data: warehouses = [], isLoading: warehousesLoading } = useActiveWarehouses();

  const [form, setForm] = useState({
    fromWarehouseId: transfer?.fromWarehouseId ?? '',
    toWarehouseId: transfer?.toWarehouseId ?? '',
    transferDate: transfer?.transferDate ? String(transfer.transferDate).slice(0, 10) : today(),
    expectedReceiptDate: transfer?.expectedReceiptDate ? String(transfer.expectedReceiptDate).slice(0, 10) : '',
    shippingMethod: transfer?.shippingMethod ?? '',
    trackingNumber: transfer?.trackingNumber ?? '',
    notes: transfer?.notes ?? '',
  });
  const [lines, setLines] = useState<FormLine[]>(
    (transfer?.items ?? []).map((item) => ({
      productId: item.productId,
      name: item.productName,
      sku: item.sku,
      unit: item.unit,
      available: item.availableInSource,
      quantity: String(item.requestedQuantity),
    })),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // A new transfer starts from the default warehouse
  useEffect(() => {
    if (!isEdit && !form.fromWarehouseId && warehouses.length > 0) {
      setForm((prev) => ({ ...prev, fromWarehouseId: warehouses.find((warehouse) => warehouse.isDefault)?.id ?? warehouses[0].id }));
    }
  }, [isEdit, form.fromWarehouseId, warehouses]);

  const [pickerInput, setPickerInput] = useState('');
  const [pickerSearch, setPickerSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setPickerSearch(pickerInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [pickerInput]);
  // Only what the source warehouse holds can be moved out of it
  const { data: sourceStock, isFetching: stockLoading } = useWarehouseStock(form.fromWarehouseId || undefined, {
    page: 1,
    limit: 8,
    search: pickerSearch || undefined,
  });
  const choices = (sourceStock?.data ?? []).filter((row) => !lines.some((line) => line.productId === row.productId));

  const fromName = warehouses.find((warehouse) => warehouse.id === form.fromWarehouseId)?.name ?? transfer?.fromWarehouse?.name ?? 'the source warehouse';
  const totalUnits = Math.round(lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0) * 10000) / 10000;

  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const changeSource = (id: string) => {
    // Stock differs by warehouse, so the lines start again
    setForm((prev) => ({ ...prev, fromWarehouseId: id, toWarehouseId: prev.toWarehouseId === id ? '' : prev.toWarehouseId }));
    if (id !== form.fromWarehouseId) setLines([]);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.fromWarehouseId) next.fromWarehouseId = 'Choose where the stock leaves from';
    if (!form.toWarehouseId) next.toWarehouseId = 'Choose where the stock goes';
    else if (form.toWarehouseId === form.fromWarehouseId) next.toWarehouseId = 'Choose a different warehouse';
    if (!form.transferDate) next.transferDate = 'Enter the transfer date';
    if (form.expectedReceiptDate && form.transferDate && form.expectedReceiptDate < form.transferDate) {
      next.expectedReceiptDate = 'This cannot be before the transfer date';
    }
    if (lines.length === 0) next.lines = 'Add at least one product';
    lines.forEach((line) => {
      const quantity = Number(line.quantity);
      if (!isQuantity(quantity) || quantity <= 0) next[`line-${line.productId}`] = 'Enter a quantity above zero, up to 4 decimals';
      else if (quantity > line.available) next[`line-${line.productId}`] = `Only ${formatNumber(line.available)} in ${fromName}`;
    });
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      fromWarehouseId: form.fromWarehouseId,
      toWarehouseId: form.toWarehouseId,
      transferDate: form.transferDate,
      expectedReceiptDate: form.expectedReceiptDate || null,
      shippingMethod: form.shippingMethod.trim() || null,
      trackingNumber: form.trackingNumber.trim() || null,
      notes: form.notes.trim() || null,
      items: lines.map((line) => ({ productId: line.productId, quantity: Number(line.quantity) })),
    });
  };

  if (!warehousesLoading && warehouses.length < 2) {
    return (
      <div className="py-6 text-center">
        <BuildingStorefrontIcon className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm font-medium text-gray-900">A transfer needs two warehouses</p>
        <p className="mt-1 text-sm text-gray-500">Add a second warehouse first, then move stock into it.</p>
        <Link to="/warehouses" className={cn(primaryButtonClass, 'mt-4')}>
          Go to Warehouses
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <FormCard title="Route" description="Where the stock leaves from and where it goes">
        <Field label="From warehouse" htmlFor="st-from" required error={errors.fromWarehouseId}>
          <select id="st-from" value={form.fromWarehouseId} onChange={(event) => changeSource(event.target.value)} className={cn(inputClass, errors.fromWarehouseId && inputErrorClass)}>
            <option value="">Select…</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name} ({warehouse.code})
              </option>
            ))}
          </select>
        </Field>
        <Field label="To warehouse" htmlFor="st-to" required error={errors.toWarehouseId}>
          <select id="st-to" value={form.toWarehouseId} onChange={set('toWarehouseId')} className={cn(inputClass, errors.toWarehouseId && inputErrorClass)}>
            <option value="">Select…</option>
            {warehouses
              .filter((warehouse) => warehouse.id !== form.fromWarehouseId)
              .map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name} ({warehouse.code})
                </option>
              ))}
          </select>
        </Field>
        <Field label="Transfer date" htmlFor="st-date" required error={errors.transferDate}>
          <input id="st-date" type="date" value={form.transferDate} onChange={set('transferDate')} className={cn(inputClass, errors.transferDate && inputErrorClass)} />
        </Field>
        <Field label="Expected receipt" htmlFor="st-expected" error={errors.expectedReceiptDate}>
          <input id="st-expected" type="date" min={form.transferDate} value={form.expectedReceiptDate} onChange={set('expectedReceiptDate')} className={cn(inputClass, errors.expectedReceiptDate && inputErrorClass)} />
        </Field>
      </FormCard>

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
          <h4 className="text-sm font-semibold text-gray-900">Products</h4>
          <p className="mt-0.5 text-xs text-gray-500">Pick from what {fromName} holds. Stock moves only when the transfer is shipped.</p>
        </div>
        <div className="space-y-3 p-4">
          <div>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                id="st-product-search"
                type="search"
                value={pickerInput}
                disabled={!form.fromWarehouseId}
                onChange={(event) => setPickerInput(event.target.value)}
                placeholder={form.fromWarehouseId ? 'Search product or SKU to add' : 'Choose the source warehouse first'}
                aria-label="Search products to add"
                className={cn(inputClass, 'pl-9')}
              />
            </div>
            {form.fromWarehouseId && (
              <ul className={cn('mt-2 divide-y divide-gray-100 rounded-md border border-gray-200', stockLoading && 'opacity-60')}>
                {choices.length === 0 ? (
                  <li className="px-3 py-3 text-sm text-gray-500">
                    {sourceStock && sourceStock.total === 0 && !pickerSearch
                      ? `${fromName} holds no stock.`
                      : sourceStock
                        ? 'No more products match.'
                        : 'Loading…'}
                  </li>
                ) : (
                  choices.map((row) => (
                    <li key={row.productId} className="flex items-center justify-between gap-3 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{row.name}</p>
                        <p className="text-xs text-gray-500">
                          {row.sku} · {formatNumber(row.quantity)} {row.unit} here
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setLines((prev) => [...prev, { productId: row.productId, name: row.name, sku: row.sku, unit: row.unit, available: row.quantity, quantity: '1' }])
                        }
                        className="shrink-0 rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50"
                        aria-label={`Add ${row.name}`}
                      >
                        Add
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          {errors.lines && <p className="text-sm text-red-600">{errors.lines}</p>}

          {lines.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className={thClass}>Product</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>In {fromName}</th>
                    <th scope="col" className={cn(thClass, 'w-40 text-right')}>Transfer</th>
                    <th scope="col" className={cn(thClass, 'w-12')}><span className="sr-only">Remove</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lines.map((line) => (
                    <tr key={line.productId}>
                      <td className="px-4 py-2.5">
                        <p className="text-sm font-medium text-gray-900">{line.name}</p>
                        <p className="text-xs text-gray-500">{line.sku}</p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-600 tabular-nums">
                        {formatNumber(line.available)} {line.unit}
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          type="number"
                          min="0"
                          step={QUANTITY_STEP}
                          max={line.available}
                          value={line.quantity}
                          onChange={(event) => {
                            setLines((prev) => prev.map((item) => (item.productId === line.productId ? { ...item, quantity: event.target.value } : item)));
                            // The message belongs to the old quantity
                            setErrors((prev) => {
                              const { [`line-${line.productId}`]: _cleared, ...rest } = prev;
                              return rest;
                            });
                          }}
                          aria-label={`Quantity of ${line.name}`}
                          className={cn(inputClass, 'text-right tabular-nums', errors[`line-${line.productId}`] && inputErrorClass)}
                        />
                        {errors[`line-${line.productId}`] && <p className="mt-1 text-right text-xs text-red-600">{errors[`line-${line.productId}`]}</p>}
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => setLines((prev) => prev.filter((item) => item.productId !== line.productId))}
                          aria-label={`Remove ${line.name}`}
                          className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-700" colSpan={2}>
                      {lines.length} product(s)
                    </td>
                    <td className="px-4 py-2.5 text-right text-sm font-semibold text-gray-900 tabular-nums">{formatNumber(totalUnits)} units</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </section>

      <FormCard title="Transport" description="Optional">
        <Field label="Sent by" htmlFor="st-method" hint="For example company vehicle or courier">
          <input id="st-method" type="text" maxLength={100} value={form.shippingMethod} onChange={set('shippingMethod')} className={inputClass} />
        </Field>
        <Field label="Vehicle / LR / tracking no." htmlFor="st-tracking">
          <input id="st-tracking" type="text" maxLength={100} value={form.trackingNumber} onChange={set('trackingNumber')} className={inputClass} />
        </Field>
        <Field label="Notes" htmlFor="st-notes" className="sm:col-span-2">
          <textarea id="st-notes" rows={2} maxLength={1000} value={form.notes} onChange={set('notes')} className={inputClass} />
        </Field>
      </FormCard>

      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Draft'}
        </button>
      </FormActions>
    </form>
  );
};

interface TransferViewProps {
  transferId: string;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (transfer: StockTransfer) => void;
  onClose: () => void;
}

// One transfer with its lines and the next step it can take
const TransferView: React.FC<TransferViewProps> = ({ transferId, canUpdate, canDelete, onEdit, onClose }) => {
  const { data: transfer, isLoading, isError, error } = useStockTransfer(transferId);
  const ship = useShipStockTransfer();
  const receive = useReceiveStockTransfer();
  const cancel = useCancelStockTransfer();
  const remove = useDeleteStockTransfer();

  const [receiving, setReceiving] = useState(false);
  const [received, setReceived] = useState<Record<string, string>>({});
  const [receiptNotes, setReceiptNotes] = useState('');
  const [confirm, setConfirm] = useState<'ship' | 'cancel' | 'delete' | null>(null);

  const items = useMemo(() => transfer?.items ?? [], [transfer]);
  const busy = ship.isPending || receive.isPending || cancel.isPending || remove.isPending;

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !transfer) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The transfer could not be loaded.')}</p>;

  const short = items.filter((item) => item.availableInSource < item.requestedQuantity);
  const receivedOf = (itemId: string, shipped: number) => (received[itemId] === undefined ? shipped : Number(received[itemId]));
  const receiveErrors = items.filter((item) => {
    const value = receivedOf(item.id, item.shippedQuantity);
    return !isQuantity(value) || value < 0 || value > item.shippedQuantity;
  });
  const shortfall = Math.round(items.reduce((sum, item) => sum + (item.shippedQuantity - (receivedOf(item.id, item.shippedQuantity) || 0)), 0) * 10000) / 10000;

  const startReceiving = () => {
    setReceived({});
    setReceiptNotes('');
    setReceiving(true);
  };

  const handleReceive = () => {
    if (receiveErrors.length > 0) return;
    const shortLines = items
      .filter((item) => receivedOf(item.id, item.shippedQuantity) !== item.shippedQuantity)
      .map((item) => ({ itemId: item.id, receivedQuantity: receivedOf(item.id, item.shippedQuantity) }));
    receive.mutate(
      { id: transfer.id, data: { items: shortLines.length ? shortLines : undefined, notes: receiptNotes.trim() || undefined } },
      {
        onSuccess: () => {
          toast.success(`${transfer.transferNumber} received into ${transfer.toWarehouse?.name ?? 'the warehouse'}`);
          setReceiving(false);
        },
        onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The transfer could not be received')),
      },
    );
  };

  const handleConfirm = () => {
    const done = () => setConfirm(null);
    if (confirm === 'ship') {
      ship.mutate(transfer.id, {
        onSuccess: () => {
          toast.success(`${transfer.transferNumber} shipped`);
          done();
        },
        onError: (actionError) => {
          toast.error(getApiErrorMessage(actionError, 'The transfer could not be shipped'));
          done();
        },
      });
    } else if (confirm === 'cancel') {
      cancel.mutate(transfer.id, {
        onSuccess: () => {
          toast.success(`${transfer.transferNumber} cancelled`);
          done();
        },
        onError: (actionError) => {
          toast.error(getApiErrorMessage(actionError, 'The transfer could not be cancelled'));
          done();
        },
      });
    } else if (confirm === 'delete') {
      remove.mutate(transfer.id, {
        onSuccess: () => {
          toast.success(`${transfer.transferNumber} deleted`);
          done();
          onClose();
        },
        onError: (actionError) => {
          toast.error(getApiErrorMessage(actionError, 'The transfer could not be deleted'));
          done();
        },
      });
    }
  };

  const draft = isDraft(transfer.status);
  const inTransit = transfer.status === 'in_transit';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 p-4 ring-1 ring-gray-200">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">From</p>
          <p className="truncate text-sm font-semibold text-gray-900">{transfer.fromWarehouse?.name ?? '—'}</p>
        </div>
        <ArrowRightIcon className="h-5 w-5 shrink-0 text-gray-400" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">To</p>
          <p className="truncate text-sm font-semibold text-gray-900">{transfer.toWarehouse?.name ?? '—'}</p>
        </div>
        <StatusBadge status={transfer.status} />
      </div>

      <DetailCard title="Transfer" columns={4}>
        <DetailItem label="Transfer date">{formatDate(transfer.transferDate)}</DetailItem>
        <DetailItem label="Expected receipt">{formatDate(transfer.expectedReceiptDate)}</DetailItem>
        <DetailItem label="Received on">{formatDate(transfer.actualReceiptDate)}</DetailItem>
        <DetailItem label="Units">{formatNumber(transfer.totalQuantity)}</DetailItem>
        <DetailItem label="Sent by">{transfer.shippingMethod || '—'}</DetailItem>
        <DetailItem label="Vehicle / tracking">{transfer.trackingNumber || '—'}</DetailItem>
        <DetailItem label="Notes" className="col-span-2">
          <span className="whitespace-pre-line font-normal">{transfer.notes || '—'}</span>
        </DetailItem>
      </DetailCard>

      {draft && short.length > 0 && (
        <p className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-amber-600/20">
          {transfer.fromWarehouse?.name} no longer holds enough of {short.map((item) => item.productName).join(', ')}. Edit the transfer before shipping.
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Product</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Requested</th>
              {draft && <th scope="col" className={cn(thClass, 'text-right')}>In source now</th>}
              {!draft && <th scope="col" className={cn(thClass, 'text-right')}>Shipped</th>}
              {!draft && <th scope="col" className={cn(thClass, 'w-36 text-right')}>Received</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item) => {
              const value = receivedOf(item.id, item.shippedQuantity);
              const invalid = receiving && (!isQuantity(value) || value < 0 || value > item.shippedQuantity);
              return (
                <tr key={item.id}>
                  <td className="px-4 py-2.5">
                    <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                    <p className="text-xs text-gray-500">{item.sku}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-900 tabular-nums">
                    {formatNumber(item.requestedQuantity)} <span className="text-gray-500">{item.unit}</span>
                  </td>
                  {draft && (
                    <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', item.availableInSource < item.requestedQuantity ? 'font-medium text-red-600' : 'text-gray-600')}>
                      {formatNumber(item.availableInSource)}
                    </td>
                  )}
                  {!draft && <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(item.shippedQuantity)}</td>}
                  {!draft && (
                    <td className="px-4 py-2.5 text-right text-sm tabular-nums">
                      {receiving ? (
                        <>
                          <input
                            type="number"
                            min="0"
                            step={QUANTITY_STEP}
                            max={item.shippedQuantity}
                            value={received[item.id] ?? String(item.shippedQuantity)}
                            onChange={(event) => setReceived((prev) => ({ ...prev, [item.id]: event.target.value }))}
                            aria-label={`Received quantity of ${item.productName}`}
                            className={cn(inputClass, 'text-right', invalid && inputErrorClass)}
                          />
                          {invalid && <p className="mt-1 text-xs text-red-600">0 to {formatNumber(item.shippedQuantity)}</p>}
                        </>
                      ) : transfer.status === 'received' ? (
                        <span className={cn(item.receivedQuantity < item.shippedQuantity ? 'font-medium text-red-600' : 'text-gray-900')}>
                          {formatNumber(item.receivedQuantity)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {receiving && (
        <div className="space-y-3">
          <Field label="Receipt note" htmlFor="st-receipt-note" hint="Optional. For example what was damaged or missing">
            <input id="st-receipt-note" type="text" maxLength={500} value={receiptNotes} onChange={(event) => setReceiptNotes(event.target.value)} className={inputClass} />
          </Field>
          {shortfall > 0 && receiveErrors.length === 0 && (
            <p className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-amber-600/20">
              {formatNumber(shortfall)} unit(s) did not arrive. They will not be added to any warehouse and stay out of stock.
            </p>
          )}
        </div>
      )}

      <FormActions>
        {receiving ? (
          <>
            <button type="button" onClick={() => setReceiving(false)} disabled={busy} className={secondaryButtonClass}>
              Back
            </button>
            <button type="button" onClick={handleReceive} disabled={busy || receiveErrors.length > 0} className={primaryButtonClass}>
              {receive.isPending ? 'Receiving…' : 'Confirm Receipt'}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onClose} disabled={busy} className={secondaryButtonClass}>
              Close
            </button>
            {draft && canDelete && (
              <button type="button" onClick={() => setConfirm('delete')} disabled={busy} className={secondaryButtonClass}>
                Delete
              </button>
            )}
            {(draft || inTransit) && canUpdate && (
              <button type="button" onClick={() => setConfirm('cancel')} disabled={busy} className={secondaryButtonClass}>
                Cancel Transfer
              </button>
            )}
            {draft && canUpdate && (
              <button type="button" onClick={() => onEdit(transfer)} disabled={busy} className={secondaryButtonClass}>
                Edit
              </button>
            )}
            {draft && canUpdate && (
              <button type="button" onClick={() => setConfirm('ship')} disabled={busy || short.length > 0} className={primaryButtonClass}>
                Ship
              </button>
            )}
            {inTransit && canUpdate && (
              <button type="button" onClick={startReceiving} disabled={busy} className={primaryButtonClass}>
                Receive
              </button>
            )}
          </>
        )}
      </FormActions>

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
        tone={confirm === 'ship' ? 'primary' : 'danger'}
        title={confirm === 'ship' ? 'Ship transfer' : confirm === 'cancel' ? 'Cancel transfer' : 'Delete transfer'}
        message={
          confirm === 'ship' ? (
            <>
              {formatNumber(transfer.totalQuantity)} unit(s) will leave <span className="font-semibold">{transfer.fromWarehouse?.name}</span> now. They are
              in transit until {transfer.toWarehouse?.name} receives them.
            </>
          ) : confirm === 'cancel' ? (
            inTransit ? (
              <>The stock that was shipped goes back into <span className="font-semibold">{transfer.fromWarehouse?.name}</span>.</>
            ) : (
              <>This draft will be closed. No stock has moved.</>
            )
          ) : (
            <>Delete draft <span className="font-semibold">{transfer.transferNumber}</span>? No stock has moved.</>
          )
        }
        confirmLabel={confirm === 'ship' ? 'Ship' : confirm === 'cancel' ? 'Cancel Transfer' : 'Delete'}
        pendingLabel="Working…"
        isPending={busy}
      />
    </div>
  );
};

const StockTransfers: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('inventory.create');
  const canUpdate = can('inventory.update');
  const canDelete = can('inventory.delete');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StockTransferStatus | ''>('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<StockTransfer | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching, refetch } = useStockTransfersPage({
    page,
    limit,
    search: search || undefined,
    status: statusFilter,
    warehouseId: warehouseFilter || undefined,
  });
  const { data: summary } = useStockTransferSummary();
  const { data: warehouses = [] } = useActiveWarehouses();

  const createTransfer = useCreateStockTransfer();
  const updateTransfer = useUpdateStockTransfer();

  const transfers = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const viewing = transfers.find((transfer) => transfer.id === viewingId);

  // Step back when the last page no longer exists
  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const hasFilters = !!searchInput || !!statusFilter || !!warehouseFilter;
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setWarehouseFilter('');
    setPage(1);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const closeForm = () => {
    if (createTransfer.isPending || updateTransfer.isPending) return;
    setFormOpen(false);
  };

  const handleSubmit = (input: StockTransferInput) => {
    const onError = (submitError: unknown) => toast.error(getApiErrorMessage(submitError, 'The transfer could not be saved'));
    if (editing) {
      updateTransfer.mutate(
        { id: editing.id, data: input },
        {
          onSuccess: (saved) => {
            toast.success(`${saved.transferNumber} updated`);
            setFormOpen(false);
            setViewingId(saved.id);
          },
          onError,
        },
      );
    } else {
      createTransfer.mutate(input, {
        onSuccess: (saved) => {
          toast.success(`${saved.transferNumber} saved as a draft`);
          setFormOpen(false);
          setViewingId(saved.id);
        },
        onError,
      });
    }
  };

  const stats = [
    { label: 'Transfers', value: summary ? formatNumber(summary.total) : '—', hint: `${summary ? formatNumber(summary.cancelled) : '—'} cancelled` },
    { label: 'Drafts', value: summary ? formatNumber(summary.draft) : '—', hint: 'Not shipped yet' },
    { label: 'In transit', value: summary ? formatNumber(summary.inTransit) : '—', hint: `${summary ? formatNumber(summary.unitsInTransit) : '—'} unit(s) on the way` },
    { label: 'Received', value: summary ? formatNumber(summary.received) : '—', hint: 'Completed' },
  ];

  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Stock Transfers</h1>
          <p className="mt-1 text-sm text-gray-500">
            Move stock between warehouses. Shipping takes it out of the source; receiving puts it into the destination.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/warehouses" className={secondaryButtonClass}>
            <BuildingStorefrontIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Warehouses
          </Link>
          {canCreate && (
            <button type="button" onClick={openAdd} className={primaryButtonClass}>
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              New Transfer
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by transfer number or warehouse"
              aria-label="Search transfers"
              className={cn(inputClass, 'pl-9')}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as StockTransferStatus | '');
              setPage(1);
            }}
            aria-label="Status"
            className={cn(inputClass, 'lg:w-44')}
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="in_transit">In transit</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={warehouseFilter}
            onChange={(event) => {
              setWarehouseFilter(event.target.value);
              setPage(1);
            }}
            aria-label="Warehouse"
            className={cn(inputClass, 'lg:w-56')}
          >
            <option value="">All warehouses</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </select>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              Clear
            </button>
          )}
        </div>

        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The transfers could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : (
          <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Transfer</th>
                  <th scope="col" className={thClass}>From</th>
                  <th scope="col" className={thClass}>To</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Products</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Units</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 7 }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : transfers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No transfer matches' : 'No stock transfers yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">
                        {hasFilters ? 'Try a different search or filter.' : 'Create a transfer to move stock from one warehouse to another.'}
                      </p>
                      {!hasFilters && canCreate && (
                        <button type="button" onClick={openAdd} className={cn(primaryButtonClass, 'mt-4')}>
                          New Transfer
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  transfers.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3">
                        <button type="button" onClick={() => setViewingId(transfer.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                          {transfer.transferNumber}
                        </button>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDate(transfer.transferDate)}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{transfer.fromWarehouse?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{transfer.toWarehouse?.name ?? '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(transfer.itemCount)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(transfer.totalQuantity)}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <StatusBadge status={transfer.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setViewingId(transfer.id)}
                            title={isDraft(transfer.status) ? 'View and ship' : transfer.status === 'in_transit' ? 'View and receive' : 'View'}
                            aria-label={`View ${transfer.transferNumber}`}
                            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>
              Showing {firstRow}–{lastRow} of {formatNumber(total)}
            </span>
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? `Edit ${editing.transferNumber}` : 'New Stock Transfer'}
        description="Saved as a draft. Stock moves when the transfer is shipped."
        size="2xl"
      >
        {formOpen && (
          <TransferForm
            key={editing?.id ?? 'new'}
            transfer={editing}
            isPending={createTransfer.isPending || updateTransfer.isPending}
            onCancel={closeForm}
            onSubmit={handleSubmit}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!viewingId && !formOpen}
        onClose={() => setViewingId(null)}
        title={viewing?.transferNumber ?? 'Stock Transfer'}
        size="2xl"
      >
        {viewingId && !formOpen && (
          <TransferView
            key={viewingId}
            transferId={viewingId}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onClose={() => setViewingId(null)}
            onEdit={(transfer) => {
              setEditing(transfer);
              setFormOpen(true);
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default StockTransfers;
